import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import { queryKeys } from '@lib/queryKeys';
import { formatDate } from '@lib/datetime';
import { webhookHealthAPI } from '@/services/webhook-health';
import { tenantIntegrationsAPI } from '@/services/tenants';
import { formatAgo, latestWebhookAt } from '../overviewModel';
import { CardStatus, OverviewCard } from './OverviewCard';

const WINDOW_HOURS = 24;

const PROVIDERS = [
  { provider: 'shopify', label: 'tenants.integrations.shopify.title' },
  { provider: 'onfleet', label: 'tenants.integrations.onfleet.title' },
] as const;

/**
 * Latest webhook per provider over 24 hours (`webhook_failures:read`); an
 * admin also sees when each credential last verified.
 */
export function IntegrationsCard({ isAdmin, className }: { isAdmin: boolean; className?: string }) {
  const { t, i18n } = useTranslation();
  const health = useQuery({
    // Same cache slot as the webhook health page.
    queryKey: queryKeys.webhookHealth.snapshot({ windowHours: WINDOW_HOURS }),
    queryFn: () => webhookHealthAPI.get(WINDOW_HOURS),
    staleTime: 15_000,
  });
  const integrations = useQuery({
    queryKey: queryKeys.tenantIntegrations.current(),
    queryFn: () => tenantIntegrationsAPI.get(),
    enabled: isAdmin,
  });
  const now = new Date();
  return (
    <OverviewCard
      title={t('overview.integrations.title')}
      link={
        isAdmin ? { to: '/settings/tenant', label: t('overview.integrations.open') } : undefined
      }
      className={className}
    >
      {health.data ? (
        PROVIDERS.map(({ provider, label }) => {
          const at = latestWebhookAt(health.data, provider);
          const verifiedAt = integrations.data?.find((s) => s.provider === provider)?.verifiedAt;
          return (
            <div
              key={provider}
              className="flex items-center justify-between gap-3 border-b py-2 last:border-b-0"
            >
              <div>
                <div className="font-medium">{t(label)}</div>
                {isAdmin && integrations.data ? (
                  <div className="text-xs text-muted-foreground">
                    {verifiedAt
                      ? t('overview.integrations.verified', { date: formatDate(verifiedAt) })
                      : t('overview.integrations.unverified')}
                  </div>
                ) : null}
              </div>
              <span className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">
                  {at
                    ? t('overview.integrations.lastWebhook', {
                        ago: formatAgo(at, now, i18n.language),
                      })
                    : t('overview.integrations.noWebhook')}
                </span>
                <span
                  aria-hidden="true"
                  data-testid={`overview-integrations-dot-${provider}`}
                  data-state={at ? 'live' : 'silent'}
                  className={cn(
                    'inline-block size-2 rounded-full',
                    at ? 'bg-success ring-[3px] ring-success/20' : 'bg-muted-foreground/40',
                  )}
                />
              </span>
            </div>
          );
        })
      ) : (
        <CardStatus
          loading={health.isPending}
          label={health.isPending ? t('overview.loading') : t('overview.loadError')}
        />
      )}
    </OverviewCard>
  );
}
